import React, { useState } from 'react';

import { FILE_TYPES_META, FileType } from '@models/file';
import { CircularProgress } from '@mui/material';
import { MAIN_THEME } from '@styles';
import { CDN_URL } from '@utils';
import { deriveMimeTypes, isFile } from '@utils/file-utils';
import { isEmpty } from '@utils/validationUtils';

import FormFieldWrapper from './FormFieldWrapper';
import { IFormFieldInputBaseProps } from '.';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

export interface IEnhancedInputProps extends IFormFieldInputBaseProps {
  value: string | File;
  loader?: boolean;
  inline?: boolean;
  allowedFileTypes?: FileType[];
  onUpload: (file: File) => void;
  onBlur?: (
    name: string,
    e: React.FocusEvent<HTMLButtonElement, Element>
  ) => void;
  downloadLinks?: {
    displayName: string;
    downloadUrl?: string;
    action?: () => void | Promise<void>;
  }[];
  downloadButton?: {
    downloadUrl: string;
    action?: () => void;
  };
  classes?: {
    label?: string;
    inputShell?: string;
  };
  showUploadButton?: boolean;
}

const useStyles = makeStyles()(() => ({
  hiddenFileInput: {
    height: 0,
    width: 0,
    margin: 0,
    padding: 0,
    visibility: 'hidden',
    border: 'none',
    display: 'block'
  },
  inputShell: {
    position: 'relative',
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '15px',
    height: 34,
    width: '100%',
    padding: '9px 34px 9px 5px',
    margin: '0 auto',
    background: 'unset',
    border: 'none',
    boxSizing: 'border-box',
    cursor: 'pointer',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: MAIN_THEME.font.regular
  },
  inputDisabled: {
    cursor: 'not-allowed',
    opacity: 0.4
  },
  inputPlaceholder: {
    fontStyle: 'italic',
    fontFamily: MAIN_THEME.font.regular,
    color: MAIN_THEME.color.white86,
    opacity: 1,
    fontSize: MAIN_THEME.fontSize.sm
  },
  iconContainer: {
    top: 'calc(50% - 15px)',
    right: '2px',
    width: '30px',
    height: '30px',
    margin: 0,
    position: 'absolute',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '80% 80%'
  },
  downloadLink: {
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '12px',
    margin: '0',
    padding: 0,
    display: 'inline-block',
    border: 'none',
    fontFamily: MAIN_THEME.font.regular,
    color: 'rgba(211, 13, 43, 0.8)',
    backgroundColor: 'transparent',
    textDecoration: 'underline',
    cursor: 'pointer'
  },
  placeholderStyle: {
    fontWeight: 100,
    color: MAIN_THEME.color.white86,
    fontStyle: 'italic',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    marginLeft: '12px'
  },
  valueStyle: {
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  },
  inputProgress: {
    opacity: 1
  },
  uploadingContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'absolute',
    right: '5px',
    top: 'calc(50% - 10px)',
    height: '20px'
  },
  progress: {
    color: MAIN_THEME.color.black,
    fontSize: MAIN_THEME.fontSize.xs,
    lineHeight: '11px'
  },
  loader: {
    display: 'block',
    width: '25px',
    height: '20px'
  },
  downloadIconContainer: {
    alignSelf: 'center',
    width: '30px',
    height: '30px'
  },
  inputDownload: {
    width: 'calc(100% - 30px)'
  },
  wrapper: {
    display: 'flex',
    boxSizing: 'border-box',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white86}`,
    borderRadius: '5px',
    width: '100%'
  },
  materialWrapper: {
    border: 'none',
    background: 'unset',
    borderRadius: 0,
    borderBottom: `1px solid ${MAIN_THEME.color.white86}`,
    marginBottom: '5px'
  },
  inputError: {
    borderColor: MAIN_THEME.color.red,
    outlineColor: MAIN_THEME.color.red
  },
  inputWrapper: {
    display: 'flex',
    position: 'relative',
    gap: '5px',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start'
  }
}));

const UploadFileFormField: React.FC<IEnhancedInputProps> = ({
  error,
  name,
  title,
  value,
  disabled,
  onUpload,
  onBlur,
  loader,
  allowedFileTypes,
  inline,
  downloadLinks = [],
  downloadButton,
  material,
  className,
  classes = {},
  placeholder,
  showUploadButton = true
}) => {
  const { classes: inputClasses } = useStyles();
  const [fileError, setFileError] = useState<string>('');
  let uploadInputElement: HTMLInputElement;
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled && event.target.files && event.target.files.length > 0) {
      const uploadedFile = event.target.files[0];
      const validationError = validateFile(uploadedFile);
      setFileError(validationError);
      if (validationError) {
        uploadInputElement.value = '';
      } else {
        uploadInputElement.value = '';
        onUpload(uploadedFile);
      }
    }
  };

  const validateFile = (file: File): string | undefined => {
    // const sizeMB = +(file.size / (1024 * 1024)).toFixed(2);
    // if (maxFileSizeInMB && sizeMB > maxFileSizeInMB) {
    //   return dictionary['general.invalid-file-size'];
    // }
    if (isEmpty(allowedFileTypes)) {
      return undefined;
    }
    if (
      !allowedFileTypes.some((t) => FILE_TYPES_META[t].mimetype === file.type)
    ) {
      return 'Invalid File Type';
    }

    return undefined;
  };

  const handleClick = () => {
    uploadInputElement.click();
  };

  const renderInput = () => {
    return (
      <>
        <div
          className={clsx(inputClasses.wrapper, {
            [inputClasses.materialWrapper]: material,
            [inputClasses.inputError]: error || fileError
          })}
        >
          <button
            type="button"
            title={
              !isEmpty(value)
                ? isFile(value)
                  ? (value as File).name
                  : (value as string)
                : 'Upload File'
            }
            onClick={handleClick}
            disabled={disabled}
            className={clsx(inputClasses.inputShell, classes.inputShell)}
            onBlur={(e) => onBlur && onBlur(name, e)}
            name={name}
          >
            {!value ? (
              <span className={inputClasses.placeholderStyle}>
                {placeholder}
              </span>
            ) : isFile(value) ? (
              (value as File).name
            ) : (
              (value as string)
            )}

            {showUploadButton && (
              <figure
                className={inputClasses.iconContainer}
                title={'Upload File'}
                style={{
                  backgroundImage: `url(${CDN_URL}/icons/upload-field-secondary.svg)`
                }}
              />
            )}
          </button>
          {!isEmpty(downloadButton) && (
            <button
              type="button"
              style={{ width: 'auto' }}
              title={
                !isEmpty(value)
                  ? isFile(value)
                    ? (value as File).name
                    : (value as string)
                  : 'Upload File'
              }
              onClick={() => {
                if (downloadButton.action) {
                  downloadButton.action();
                } else if (downloadButton.downloadUrl) {
                  window.open(downloadButton.downloadUrl);
                }
              }}
              disabled={disabled}
              className={clsx(inputClasses.inputShell, classes.inputShell)}
              onBlur={(e) => onBlur && onBlur(name, e)}
            >
              {downloadButton.downloadUrl && (
                <figure
                  className={inputClasses.iconContainer}
                  title={'Upload File'}
                  style={{
                    backgroundImage: `url(${CDN_URL}/icons/download-primary.svg)`
                  }}
                />
              )}
            </button>
          )}
        </div>
        <input
          type="file"
          name={name}
          className={inputClasses.hiddenFileInput}
          accept={deriveMimeTypes(allowedFileTypes)}
          ref={(el: HTMLInputElement) => {
            uploadInputElement = el;
          }}
          onChange={handleChange}
          disabled={disabled}
        />
      </>
    );
  };

  const renderDownloadLinks = () =>
    downloadLinks &&
    downloadLinks.map((link) => (
      <button
        key={link.downloadUrl}
        type="button"
        className={inputClasses.downloadLink}
        onClick={async () => {
          if (link.action) {
            await link.action();
          } else if (link.downloadUrl) {
            window.open(link.downloadUrl, '_blank');
          }
        }}
      >
        {link.displayName}
      </button>
    ));

  return inline ? (
    renderInput()
  ) : (
    <FormFieldWrapper
      title={title}
      error={error || fileError}
      name={name}
      className={className}
      classes={classes}
    >
      <div className={inputClasses.inputWrapper}>
        {renderInput()}
        {renderDownloadLinks()}
        {loader && (
          <CircularProgress
            className={inputClasses.loader}
            size={20}
            thickness={3}
          />
        )}
      </div>
    </FormFieldWrapper>
  );
};

export default UploadFileFormField;
